'use client';

import { useEffect, useRef, useState } from 'react';
import { useUnifiedUnreadCount } from '../hooks/queries/useNotifications';
import { syncAppBadge } from '../lib/appBadge';
import { getAuthSession, subscribeAuthSession } from '../lib/authSession';
import { dismissAllWebPush } from '../lib/notifications/dismissWebPush';

const PREFIX_PATTERN = /^\((\d+)\)\s*/;

/**
 * 통합 알림 unread count를 `document.title`의 `(N)` prefix로 반영한다.
 *
 * - 브라우저: Chrome 탭 등에 (N) 표시
 * - 데스크탑 앱: 메인 프로세스가 `page-title-updated` 이벤트로 N을 읽어 Dock/Taskbar badge로 반영
 *
 * 표준 SaaS UX(Slack/Linear/Discord 등). 토큰이 없으면 0으로 처리되어 prefix가 제거된다.
 */
export function DocumentTitleUnread(): null {
  const [token, setToken] = useState<string | null>(() => getAuthSession()?.token ?? null);

  // 로그인/로그아웃 변화 시 token을 갱신해 query enabled 토글.
  useEffect(() => {
    return subscribeAuthSession(() => {
      setToken(getAuthSession()?.token ?? null);
    });
  }, []);

  const { data: count = 0, isSuccess } = useUnifiedUnreadCount(token);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const base = document.title.replace(PREFIX_PATTERN, '');
    const next = count > 0 ? `(${count}) ${base}` : base;
    if (document.title !== next) document.title = next;
    syncAppBadge(count);
  }, [count]);

  // 토큰별로 최초 unread count 서버 확인 시점에 0이면 표시 중인 웹푸시를 닫는다.
  // 로딩 기본값 0이 실제 0으로 오인되지 않도록 isSuccess를 게이트로 사용한다.
  const initialDismissTokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!token) {
      initialDismissTokenRef.current = null;
      return;
    }
    if (!isSuccess) return;
    if (initialDismissTokenRef.current === token) return;
    initialDismissTokenRef.current = token;
    if (count === 0) {
      void dismissAllWebPush();
    }
  }, [token, isSuccess, count]);

  return null;
}
